import { api } from './api';

export interface CalendarReviewer {
  id: number;
  name: string;
  email: string;
  title?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  timezone: string;
  working_hours?: Record<string, Array<{ start: string; end: string }>> | null;
  slot_duration_minutes: number;
  buffer_minutes: number;
  min_notice_hours: number;
  max_days_ahead: number;
  is_active: boolean;
  is_default: boolean;
  google_calendar_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CalendarReviewerCreate {
  name: string;
  email: string;
  title?: string;
  bio?: string;
  avatar_url?: string;
  timezone?: string;
  working_hours?: Record<string, Array<{ start: string; end: string }>>;
  slot_duration_minutes?: number;
  buffer_minutes?: number;
  min_notice_hours?: number;
  max_days_ahead?: number;
  is_active?: boolean;
  is_default?: boolean;
  google_calendar_id?: string;
}

export interface AvailableSlot {
  start_time: string;
  end_time: string;
  formatted_time: string;
  reviewer_id: number;
  reviewer_name: string;
  reviewer_title?: string | null;
}

export interface DaySlots {
  date: string;
  slots: AvailableSlot[];
}

export interface AvailableSlotsResponse {
  days: DaySlots[];
  reviewer: CalendarReviewer;
  timezone: string;
}

export interface TabletopAppointment {
  id: number;
  exercise_id: number;
  reviewer_id: number;
  reviewer_name: string;
  reviewer_email: string;
  reviewer_title?: string | null;
  user_id: number;
  client_name?: string;
  client_email?: string;
  start_time: string;
  end_time: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  google_event_id?: string | null;
  google_meet_link?: string | null;
  client_notes?: string | null;
  cancellation_reason?: string | null;
  created_at?: string;
}

export interface BookAppointmentPayload {
  start_time: string;
  reviewer_id?: number;
  client_notes?: string;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  calendar_id: string;
  mode: string;
}

class CalendarService {
  // Admin Endpoints
  async listReviewers(): Promise<{ reviewers: CalendarReviewer[]; total: number }> {
    const { data } = await api.get('/v1/admin/calendar-reviewers');
    return data;
  }

  async createReviewer(payload: CalendarReviewerCreate): Promise<CalendarReviewer> {
    const { data } = await api.post('/v1/admin/calendar-reviewers', payload);
    return data;
  }

  async updateReviewer(id: number, payload: Partial<CalendarReviewerCreate>): Promise<CalendarReviewer> {
    const { data } = await api.patch(`/v1/admin/calendar-reviewers/${id}`, payload);
    return data;
  }

  async deleteReviewer(id: number): Promise<{ message: string }> {
    const { data } = await api.delete(`/v1/admin/calendar-reviewers/${id}`);
    return data;
  }

  async testConnection(reviewerId: number): Promise<TestConnectionResponse> {
    const { data } = await api.post(`/v1/admin/calendar-reviewers/${reviewerId}/test-connection`);
    return data;
  }

  async listAllAppointments(statusFilter?: string): Promise<{ appointments: TabletopAppointment[]; total: number }> {
    const params = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
    const { data } = await api.get(`/v1/admin/tabletop-appointments${params}`);
    return data;
  }

  // Client Tabletop Endpoints
  async getAvailableSlots(exerciseId: number, days = 14, reviewerId?: number): Promise<AvailableSlotsResponse> {
    const params = new URLSearchParams();
    params.set('days', String(days));
    if (reviewerId) params.set('reviewer_id', String(reviewerId));
    const { data } = await api.get(`/v1/tabletop-exercises/${exerciseId}/available-slots?${params.toString()}`);
    return data;
  }

  async getExerciseAppointment(exerciseId: number): Promise<TabletopAppointment | null> {
    const { data } = await api.get(`/v1/tabletop-exercises/${exerciseId}/appointment`);
    return data;
  }

  async bookAppointment(exerciseId: number, payload: BookAppointmentPayload): Promise<TabletopAppointment> {
    const { data } = await api.post(`/v1/tabletop-exercises/${exerciseId}/book-appointment`, payload);
    return data;
  }

  async cancelAppointment(exerciseId: number, reason?: string): Promise<{ message: string }> {
    const { data } = await api.post(`/v1/tabletop-exercises/${exerciseId}/cancel-appointment`, { reason });
    return data;
  }
}

export const calendarService = new CalendarService();
